import type { SalesforceDestinationField } from '@/modules/salesforce/formMapping';
import { destinationSnapshot, isSalesforceDestinationField } from '@/modules/salesforce/formMapping';
import type { SalesforceFieldDescription, SalesforceObjectDescription } from '@/modules/salesforce/metadata';
import { WEALTHHUB_TRUST_REVIEW_OBJECT } from '@/modules/salesforce/metadata';
import type { FormDraftInput, FormField, FormFieldType, FormSection } from '../AdminFormsPage.model';
export interface SalesforceObjectSummary {
  apiName: string;
  label: string;
  custom: boolean;
  createable: boolean;
}
export type SalesforceField = SalesforceFieldDescription;
export type SalesforceDescription = SalesforceObjectDescription;
export interface SalesforceLayoutSection {
  id: string;
  label: string;
  fieldApiNames: string[];
}
export interface SalesforceLayout {
  id: string;
  label: string;
  sections: SalesforceLayoutSection[];
}
export interface ImportField extends SalesforceDestinationField {
  formFieldType: FormFieldType;
  required: boolean;
}
export interface ImportPreview {
  fields: ImportField[];
  blockingRequiredFields: SalesforceField[];
  sections: SalesforceLayoutSection[];
}
const FIELD_TYPES: Partial<Record<string, FormFieldType>> = {
  string: 'shortText',
  textarea: 'longText',
  date: 'date',
  picklist: 'singleSelect'
};
const RECORD_TYPE_FIELD = 'RecordTypeId';
const WEALTHHUB_ACCOUNT_OBJECT = 'Account';
export function usesWealthHubDestination(description: SalesforceDescription, householdObjectApiName?: string): boolean {
  return description.apiName === WEALTHHUB_TRUST_REVIEW_OBJECT && householdObjectApiName === WEALTHHUB_ACCOUNT_OBJECT;
}
function isRequired(field: SalesforceField): boolean {
  return field.createable && !field.nillable && !field.calculated && !field.autoNumber && !field.defaultedOnCreate;
}
export function buildImportPreview(description: SalesforceDescription, layout?: SalesforceLayout, householdObjectApiName?: string): ImportPreview {
  const fields = description.fields.flatMap<ImportField>(field => {
    const formFieldType = FIELD_TYPES[field.type];
    return field.createable && !field.calculated && !field.autoNumber && isSalesforceDestinationField(field) && formFieldType ? [{
      ...field,
      formFieldType,
      required: isRequired(field)
    }] : [];
  });
  const importableNames = new Set(fields.map(field => field.apiName));
  const seen = new Set<string>();
  const sections = (layout?.sections ?? []).map(section => ({
    ...section,
    label: section.label || 'Untitled section',
    fieldApiNames: section.fieldApiNames.filter(name => {
      if (!importableNames.has(name) || seen.has(name)) return false;
      seen.add(name);
      return true;
    })
  }));
  const unplaced = fields.map(field => field.apiName).filter(name => !seen.has(name));
  if (!layout) {
    sections.push({
      id: 'imported-fields',
      label: 'Imported fields',
      fieldApiNames: unplaced
    });
  } else if (unplaced.length > 0) {
    sections.push({
      id: 'other-fields',
      label: 'Other fields',
      fieldApiNames: unplaced
    });
  }
  const orderedNames = sections.flatMap(section => section.fieldApiNames);
  const fieldByName = new Map(fields.map(field => [field.apiName, field]));
  const householdLookup = uniqueHouseholdLookup(description, householdObjectApiName);
  return {
    fields: orderedNames.flatMap(name => {
      const field = fieldByName.get(name);
      return field ? [field] : [];
    }),
    sections,
    blockingRequiredFields: description.fields.filter(field => isRequired(field) && !importableNames.has(field.apiName) && field.apiName !== householdLookup?.apiName && !(usesWealthHubDestination(description, householdObjectApiName) && field.apiName === RECORD_TYPE_FIELD))
  };
}
export function createSalesforceDraft(description: SalesforceDescription, preview: ImportPreview, selectedNames: ReadonlySet<string>, sectionByField: Record<string, string>, householdObjectApiName?: string): FormDraftInput {
  const householdLookup = uniqueHouseholdLookup(description, householdObjectApiName);
  const hierarchical = usesWealthHubDestination(description, householdObjectApiName);
  const mapSalesforce = hierarchical || householdLookup !== undefined;
  const fieldByName = new Map(preview.fields.map(field => [field.apiName, field]));
  const sections: FormSection[] = preview.sections.flatMap(section => {
    const children = preview.fields.filter(field => selectedNames.has(field.apiName) && sectionByField[field.apiName] === section.id).map(field => toFormField(field, mapSalesforce));
    return children.length ? [{
      id: crypto.randomUUID(),
      type: 'section',
      label: section.label,
      children
    }] : [];
  });
  // Preserve metadata order when a caller supplies a field not assigned above.
  const missing = [...selectedNames].map(name => fieldByName.get(name)).filter((field): field is ImportField => field !== undefined && !preview.sections.some(section => section.id === sectionByField[field.apiName]));
  if (missing.length > 0) {
    sections.push({
      id: crypto.randomUUID(),
      type: 'section',
      label: 'Other fields',
      children: missing.map(field => toFormField(field, mapSalesforce))
    });
  }
  const draftConfig: FormDraftInput['draftConfig'] = {
    extractionContext: {
      prompt: ''
    },
    form: sections
  };
  if (mapSalesforce) {
    draftConfig.exports = {
      salesforce: {
        objectAPIName: description.apiName,
        householdLookup: {
          fieldAPIName: hierarchical ? '' : householdLookup?.apiName ?? ''
        }
      }
    };
  }
  return {
    name: `${description.label} form`,
    description: `Created from the ${description.label} Salesforce layout.`,
    draftConfig
  };
}
function toFormField(field: ImportField, mapSalesforce: boolean): FormField {
  const seenOptionValues = new Set<string>();
  const options = field.picklistValues.filter(option => {
    if (!option.active || seenOptionValues.has(option.value)) return false;
    seenOptionValues.add(option.value);
    return true;
  }).map(({
    label,
    value
  }) => ({
    label,
    value
  }));
  return {
    id: crypto.randomUUID(),
    type: 'field',
    fieldType: field.formFieldType,
    label: field.label,
    ...(field.helpText ? {
      helpText: field.helpText
    } : {}),
    ...(options.length ? {
      options
    } : {}),
    ...((field.formFieldType === 'shortText' || field.formFieldType === 'longText') && field.length > 0 ? {
      validation: {
        maxLength: field.length
      }
    } : {}),
    extraction: {
      prompt: `Extract ${field.label} from the reviewed documents.`
    },
    ...(mapSalesforce ? {
      exports: {
        salesforce: {
          fieldAPIName: field.apiName,
          destination: destinationSnapshot(field),
          sentAs: options.map(option => ({
            formValue: option.value,
            salesforceValue: option.value
          }))
        }
      }
    } : {})
  };
}
export function uniqueHouseholdLookup(description: SalesforceDescription, householdObjectApiName?: string): SalesforceField | undefined {
  // TDR context is selected at export time, never written to an Account lookup.
  if (!householdObjectApiName || description.apiName === WEALTHHUB_TRUST_REVIEW_OBJECT) return undefined;
  const candidates = description.fields.filter(field => field.createable && !field.calculated && !field.autoNumber && field.type === 'reference' && field.referenceTo.includes(householdObjectApiName));
  return candidates.length === 1 ? candidates[0] : undefined;
}